import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, formatDateTime as formatDateTimeGlobal, APP_CONFIG } from '../../../config/appConfig';
import { StatusDotPill } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { deleteSupplierEnquiry } from '../../../services/mutations';
import type { SupplierEnquiry } from './useData';
import { supplyBlockedActionMessage } from '../blockingMessages';

export const moduleConfig = {
  module: {
    title: 'RFQ',
    permissionModuleKey: 'supplier_enquiries',
    route: {
      list: '/supplies/supplier-enquiries',
      new: '/supplies/supplier-enquiries/new',
      detail: (id: number) => `/supplies/supplier-enquiries/${id}`,
      edit: (id: number) => `/supplies/supplier-enquiries/${id}/edit`},
    api: {
      list: '/supplier-enquiries',
      detail: (id: number) => `/supplier-enquiries/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference, notes, case or requisition…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['ref', 'vessel', 'caseRef', 'enqDate', 'requisitionRef', 'supplier']},
  ],
  filters: {
    fields: [
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: SupplierEnquiry) => item.ref || '',
        options: []},
      {
        key: 'vessel',
        label: 'Vessel',
        type: 'select' as const,
        placeholder: 'All vessels',
        getValue: (item: SupplierEnquiry) =>
          item.supplyCase?.vessel?.vesselName?.trim() || item.requisition?.vessel?.vesselName?.trim() || '',
        options: []},
      {
        key: 'caseRef',
        label: 'Case',
        type: 'select' as const,
        placeholder: 'All cases',
        getValue: (item: SupplierEnquiry) => item.supplyCase?.ref || '',
        options: []},
      {
        key: 'enqDate',
        label: 'Enquiry date',
        type: 'date' as const,
        placeholder: 'All dates',
        getValue: (item: SupplierEnquiry) => item.enqDate || '',
        options: []},
      {
        key: 'requisitionRef',
        label: 'Requisition ref',
        type: 'select' as const,
        placeholder: 'All requisitions',
        getValue: (item: SupplierEnquiry) => item.requisition?.ref || '',
        options: []},
      {
        key: 'supplier',
        label: 'Supplier',
        type: 'select' as const,
        placeholder: 'All suppliers',
        getValue: (item: SupplierEnquiry) => item.supplier?.name || '',
        options: []},
    ],
    applyFilter: (
      items: SupplierEnquiry[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      return items.filter((item) => {
        const refFilters = asArray(filters.ref);
        if (refFilters.length) {
          const v = item.ref || '';
          if (!refFilters.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }

        const vesselFilters = asArray(filters.vessel);
        if (vesselFilters.length) {
          const vesselName =
            item.supplyCase?.vessel?.vesselName?.toLowerCase() || item.requisition?.vessel?.vesselName?.toLowerCase() || '';
          if (!vesselFilters.some((f) => vesselName.includes(String(f).toLowerCase()))) return false;
        }

        const caseFilters = asArray(filters.caseRef);
        if (caseFilters.length) {
          const caseRef = (item.supplyCase?.ref || '').toLowerCase();
          if (!caseFilters.some((f) => caseRef.includes(String(f).toLowerCase()))) return false;
        }

        const enqDateFilter = dateRanges?.enqDate;
        if (enqDateFilter?.start || enqDateFilter?.end) {
          const itemDateRaw = item.enqDate ? new Date(item.enqDate) : null;
          if (!itemDateRaw || Number.isNaN(itemDateRaw.getTime())) return false;
          if (enqDateFilter.start && itemDateRaw < enqDateFilter.start) return false;
          if (enqDateFilter.end && itemDateRaw > enqDateFilter.end) return false;
        } else {
          const enqDateValues = asArray(filters.enqDate);
          if (enqDateValues.length) {
            const itemDate = toDateOnly(item.enqDate);
            if (!enqDateValues.some((f) => itemDate.includes(String(f).slice(0, 10)))) return false;
          }
        }

        const reqFilters = asArray(filters.requisitionRef);
        if (reqFilters.length) {
          const reqRef = (item.requisition?.ref || '').toLowerCase();
          if (!reqFilters.some((f) => reqRef.includes(String(f).toLowerCase()))) return false;
        }

        const supplierFilters = asArray(filters.supplier);
        if (supplierFilters.length) {
          const supplierName = (item.supplier?.name || '').toLowerCase();
          if (!supplierFilters.some((f) => supplierName.includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    },
    applySearch: (items: SupplierEnquiry[], searchTerm: string) => {
      const q = searchTerm.toLowerCase();
      return items.filter((item) => {
        const parts = [
          item.ref,
          item.notes,
          item.supplyCase?.ref,
          item.requisition?.ref,
          String(item.id),
          String(item.supplierId),
          item.supplier?.name,
          item.supplier?.code,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return parts.includes(q);
      });
    }},
  toolbar: {
    actions: []},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: SupplierEnquiry) => item.id,
  },
  emptyStates: {
    noData: 'Create an RFQ to get started.',
    noSearchResults: 'No enquiries match your search.',
    noFilterResults: 'No enquiries match the filters.',
  },
  delete: {
    title: 'Delete RFQ',
    message:
      'Delete this enquiry and all its lines? Not allowed from this list while the RFQ is on a supply case (delete it from the case activity view) or while a quotation is linked.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete RFQ.',
    handler: async (id: number) => {
      await deleteSupplierEnquiry(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  form: {
    new: { title: 'New RFQ', subtitle: 'Custom form', submitText: 'Create' },
    edit: { title: 'Edit RFQ', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.'},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

/** Path must stay in sync with `moduleConfig.route.caseView` in `supply-cases/config.tsx`. */
function supplierEnquiryCaseIdForView(row: SupplierEnquiry | undefined): number | null {
  const id = row?.supplyCase?.id ?? row?.caseId ?? 0;
  return id > 0 ? id : null;
}

const EnquiryCaseColumnCell: React.FC<{ row: SupplierEnquiry }> = ({ row }) => {
  const { colors } = useTheme();
  const caseId = supplierEnquiryCaseIdForView(row);
  if (caseId == null) {
    return (
      <div className="max-w-[16rem] py-0.5">
        <span className={cn('text-sm', colors.textSecondary)}>—</span>
      </div>
    );
  }
  const displayRef = row.supplyCase?.ref?.trim() || `#${caseId}`;
  const href = `/supplies/supply-cases/${caseId}/case-view`;
  const iconBtnClass =
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900 dark:text-indigo-400 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300';

  return (
    <div className="flex max-w-[16rem] items-center gap-0.5 py-0.5">
      <Link
        to={href}
        className={iconBtnClass}
        title="Open case view"
        aria-label="Open case view"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </Link>
      <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)} title={displayRef}>
        {displayRef}
      </span>
    </div>
  );
};

function rfqStatusDotClass(submittedAt: string | null | undefined): string {
  return submittedAt ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-amber-500 dark:bg-amber-400';
}

const RfqStatusCell: React.FC<{ row: SupplierEnquiry }> = ({ row }) => {
  const isSubmitted = Boolean(row.rfqPublicSubmittedAt);
  const label = isSubmitted ? 'Submitted' : 'Draft';
  const dotClass = rfqStatusDotClass(row.rfqPublicSubmittedAt);
  return (
    <div className="max-w-[10rem] py-0.5">
      <StatusDotPill
        dotClassName={dotClass}
        title={isSubmitted ? 'RFQ submitted' : 'RFQ draft'}
      >
        {label}
      </StatusDotPill>
    </div>
  );
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SupplierEnquiry>[] => [
  {
    key: 'vessel',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) =>
      <TextCell value={row?.supplyCase?.vessel?.vesselName ?? row?.requisition?.vessel?.vesselName ?? ''} />},
  { key: 'ref', header: 'Reference', sortable: true, render: (v, row) => <TextCell value={v ?? row?.ref} /> },
  { key: 'enqDate', header: 'Enquiry date', sortable: true, render: (v) => formatDate(v) },
  {
    key: 'supplyCase.ref',
    header: 'Case',
    sortable: true,
    maxWidth: '16rem',
    render: (_v, row) => (row ? <EnquiryCaseColumnCell row={row} /> : <TextCell value="" />)},
  {
    key: 'requisition.ref',
    header: 'Requisition',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.requisition?.ref ?? (row?.requisitionId ? `#${row.requisitionId}` : '')} />},
  {
    key: 'supplier.name',
    header: 'Supplier',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={
          row?.supplier?.name
            ? row.supplier.name
            : row?.supplierId != null
              ? String(row.supplierId)
              : ''
        }
      />
    )},
  {
    key: 'rfqStatus',
    header: 'RFQ status',
    sortable: false,
    render: (_v, row) => (row ? <RfqStatusCell row={row} /> : <TextCell value="" />)},
  {
    key: 'rfqPublicSubmittedAt',
    header: 'Submitted at',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.rfqPublicSubmittedAt ? formatDateTimeGlobal(row.rfqPublicSubmittedAt) : ''} />},
  {
    key: 'lines',
    header: 'Lines',
    sortable: true,
    render: (_v, row) => <TextCell value={String(row?._count?.lines ?? row?.lines?.length ?? 0)} />},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

const ENQUIRY_LIST_DELETE_ERR = {
  quotation: supplyBlockedActionMessage('ENQ_DELETE_LINKED_QUOTATION'),
  case: 'This RFQ is linked to a supply case. Delete it from that supply case activity list (case view), not from this list.'} as const;

const ENQUIRY_LIST_EDIT_ERR = {
  quotation: supplyBlockedActionMessage('ENQ_EDIT_LINKED_QUOTATION')} as const;

function supplierEnquiryListDeleteBlockedByQuotation(item: SupplierEnquiry): boolean {
  return (item._count?.quotations ?? 0) > 0;
}

/** Supplier enquiries always have a `caseId` in the API; list delete is only for edge cases (e.g. legacy). */
function supplierEnquiryListDeleteBlockedByCase(item: SupplierEnquiry): boolean {
  return (item.caseId ?? 0) > 0;
}

const TableActionsCell: React.FC<{
  item: SupplierEnquiry;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const { showError } = useNotification();
  const blockedByQuotation = supplierEnquiryListDeleteBlockedByQuotation(item);
  const blockedByCase = supplierEnquiryListDeleteBlockedByCase(item);
  const editBlockedByQuotation = blockedByQuotation;
  const allowsListDelete = !blockedByQuotation && !blockedByCase;
  const deleteEnabled = canDelete && allowsListDelete;
  const editEnabled = canEdit && !editBlockedByQuotation;

  const onDeleteBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (blockedByQuotation) {
      showError(`Cannot delete: ${ENQUIRY_LIST_DELETE_ERR.quotation}`);
      return;
    }
    if (blockedByCase) {
      showError(`Cannot delete: ${ENQUIRY_LIST_DELETE_ERR.case}`);
    }
  };
  const onEditBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editBlockedByQuotation) {
      showError(`Cannot edit: ${ENQUIRY_LIST_EDIT_ERR.quotation}`);
    }
  };

  return (
    <>
      <div className="flex items-center flex-nowrap">
        <Link
          to={moduleConfig.module.route.detail(item.id)}
          className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="View"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </Link>
        {canEdit && editEnabled ? (
          <Link
            to={moduleConfig.module.route.edit(item.id)}
            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </Link>
        ) : canEdit ? (
          <button
            type="button"
            onClick={onEditBlocked}
            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        ) : null}
        {canDelete && deleteEnabled ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.id);
            }}
            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ) : canDelete ? (
          <button
            type="button"
            onClick={onDeleteBlocked}
            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ) : null}
      </div>
    </>
  );
};

export const getTableActions = (
  item: SupplierEnquiry,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
